import { Component, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { FormBuilder, FormGroup } from "@angular/forms";
import { HttpHeaders, HttpParams } from "@angular/common/http";
import { lastValueFrom } from "rxjs";
import { TableAction } from "src/app/shared/components/table-paginator/table-paginator.component";
import { FirmaStateService } from "src/app/core/states/firma/firma-state.service";
import { PlantillasService } from "src/app/core/services/plantilla.service";
import { Router } from "@angular/router";
import Swal, { SweetAlertIcon } from "sweetalert2";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { AuthService } from "src/app/core/services/AuthService";
import { PermisosModalComponent } from "src/app/shared/components/permisos-acceso/permisos-modal.component";
import { SearchCertificateComponent } from "src/app/shared/components/search-certificate/search-certificate";

interface Item {
  id?: string;
  nombre: string;
  deletedAt?: string;
  estado: string;
  fecha_creacion: string;
  publica: boolean;
}

@Component({
  selector: "app-adminplantilla-component",
  templateUrl: "./admin-plantilla.component.html",
  styleUrls: ["./admin-plantilla.css"],
})
export class AdminPlantillaComponent implements OnInit {
  @ViewChild("modalAcceso") modalAccesoTemplate!: TemplateRef<any>;
  @ViewChild(SearchCertificateComponent)
  searchCertificateComponent!: SearchCertificateComponent;

  fullData: Item[] = []; // Tu conjunto completo de datos
  currentItems: Item[] = []; // Los datos para la página actual

  currentPage: number = 1;
  pageSize: number = 10; // Tamaño de página inicial
  totalItems: number = 0;
  spinner: boolean;
  accesUser: boolean = false;

  tableHeaders = [
    { key: "nombre", label: "Nombre" },
    { key: "fecha_creacion", label: "Fecha Creación" },
    { key: "tipo_acceso", label: "Tipo Acceso" },
    { key: "estado", label: "Estado" },
  ];

  customActions: TableAction[] = [];
  // Toast
  toast = Swal;

  searchForm!: FormGroup;

  constructor(
    private firmaService: FirmaStateService,
    private authService: AuthService,
    private plantillaService: PlantillasService,
    private router: Router,
    private fb: FormBuilder,
    private modalService: NgbModal,
  ) {
    this.accesUser = this.authService.hasRoleAuth("ADMINISTRADOR");

    this.customActions = [
      {
        label: "Editar",
        handler: (item: Item) => this.showLote(item.id),
        icon: "fa-solid fa-pen-to-square",
        colorClass: "bg-orange",
        visible: (item: Item) =>
          item.estado === "Activo" &&
          this.authService.canManageItem(item, this.accesUser),
      },
      {
        label: "Eliminar",
        handler: (item: Item) => this.eliminarPlantilla(item.id),
        icon: "fa-solid fa-trash",
        colorClass: "bg-red",
        visible: (item: Item) =>
          item.estado === "Activo" &&
          this.authService.canManageItem(item, this.accesUser),
      },
      {
        label: "Gestionar Acceso",
        handler: (item: Item) => this.gestionarAcceso(item.id),
        icon: "fa-solid fa-lock",
        colorClass: "bg-blue",
        visible: (item: Item) =>
          item &&
          item.estado === "Activo" &&
          this.authService.canManageItem(item, this.accesUser),
      },
      {
        label: "Activar plantilla",
        handler: (item: Item) => this.activarPlantilla(item),
        icon: "fa-solid fa-ban",
        colorClass: "bg-gray",
        visible: (item: Item) =>
          item.estado === "Inactivo" &&
          this.authService.canManageItem(item, this.accesUser),
      },
    ];
  }

  ngOnInit() {
    this.searchForm = this.fb.group({
      search: [""],
    });

    this.toastAlert();
    this.getPlantilas();
  }

  async gestionarAcceso(id: any) {
    const modalRef = this.modalService.open(PermisosModalComponent, {
      centered: true,
      size: "xl",
    });
    modalRef.componentInstance.id = id;
    modalRef.componentInstance.modeloAcceso = "Plantilla";
  }

  async createPlantilla() {
    this.router.navigate(["firmas/plantillas"]);
  }

  async getPlantilas(search: string = "") {
    this.spinner = true;
    let params = new HttpParams();
    params = params
      .set("page", this.currentPage)
      .set("limit", this.pageSize)
      .set("pagination", true)
      .set("nombre", search)
      .set("with_deleted", true);
    const result = await lastValueFrom(
      this.plantillaService.get<any[]>({ params }),
    );
    this.spinner = false;
    const headers: HttpHeaders = result.headers;
    this.currentItems = result.body.map((x: any, index: number) => {
      return {
        id: x.id,
        publica: x.publica,
        tipo_acceso: x.publica ? "Publica" : "Personalizado",
        nombre: x.nombre,
        estado: x.deletedAt === null ? "Activo" : "Inactivo",
        deletedAt: x.deletedAt,
        fecha_creacion: this.formatFechaLocalSinUTC(x.createdAt),
      };
    });
    this.totalItems = Number(headers.get("X-Total-Count"));
  }

  buscarPlantilla() {
    this.currentPage = 1;
    const texto = this.searchForm.value.search?.trim();
    if (!texto) {
      this.getPlantilas();
      return;
    }
    this.getPlantilas(texto);
  }

  async showLote(id: any) {
    this.router.navigateByUrl("firmas/plantillas/" + id);
  }

  async eliminarPlantilla(id: any) {
    Swal.fire({
      title: "¡Eliminación plantilla!",
      text: "¿Deseas eliminar esta plantilla?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Eliminar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await lastValueFrom(this.plantillaService.deletePlantilla(id));
        this.getPlantilas();
        this.alert("success", "Plantilla eliminada correctamente");
      }
    });
  }

  async activarPlantilla(item: any) {
    Swal.fire({
      title: "¡Activar plantilla!",
      text: "Deseas Activar esta plantilla",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Activar",
      cancelButtonText: "Cancelar",
    }).then(async (result) => {
      if (result.isConfirmed) {
        await lastValueFrom(this.plantillaService.activarPlantilla(item.id));
        this.alert("success", "Plantilla activada correctamente");
        this.getPlantilas();
      }
    });
  }

  onPageChange(newPage: number): void {
    this.currentPage = newPage;
    this.getPlantilas();
  }

  onPageSizeChange(newSize: number): void {
    this.pageSize = newSize;
    this.currentPage = 1; // Siempre vuelve a la primera página al cambiar el tamaño
    this.getPlantilas();
  }

  toastAlert() {
    this.toast = Swal.mixin({
      toast: true,
      position: "top-end",
      showConfirmButton: false,
      timer: 3000,
      timerProgressBar: true,
      didOpen: (toast) => {
        toast.onmouseenter = Swal.stopTimer;
        toast.onmouseleave = Swal.resumeTimer;
      },
    });
  }

  alert(icontext: SweetAlertIcon, title: string, text: string = "") {
    this.toast.fire({
      icon: icontext,
      title: title,
      text: text,
    });
  }

  formatFechaLocalSinUTC(iso: string): string {
    // Quitamos la Z y los milisegundos
    const limpia = iso.replace("Z", "").split(".")[0];

    const d = new Date(limpia);

    const pad = (n: number) => n.toString().padStart(2, "0");

    return (
      `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()} ` +
      `${pad(d.getHours())}:${pad(d.getMinutes())}`
    );
  }
}
